// cSpell:ignore Aandeel amts Eigenaar kost laste Overschot
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatNumber } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  Banner,
  DataTable,
  Modal,
  Select,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { LocaleNumberField } from "../../../../components/LocaleNumberField";
import {
  allocate,
  daysInPeriod,
  mergedCoverage,
  occupantKey,
  overlapDays,
} from "../../../../engine";
import { keyDisplayName } from "../../../../keyName.utils";
import { fromCents, sumInCents, toCents } from "../../../../money.utils";
import { MONEY_DECIMALS } from "../../../../numberInput.utils";
import { formatShare, ownersShareByKey } from "../../../../ownerShare.utils";
import {
  ApportionmentKey,
  Category,
  CATEGORY_LABELS_NL,
  CONSUMPTION_UNIT,
  ContractRef,
  DistributionMethod,
  methodLabel,
  SettlementDraftState,
} from "../../../../types";
import { fmtDate, rentedPeriodLabel } from "./dates";
import { fmt, moneyCell } from "./shared";

type UnitRef = {
  id: string;
  code: string;
  address?: string;
  archivedOn?: string;
};

export interface DistributionEditorModalProps {
  category: Category;
  method: DistributionMethod;
  total: number;
  // The period the category's costs cover: decides which tenants and which
  // vacant stretches get a line.
  period: { from: string; to: string };
  contracts: ContractRef[];
  units: UnitRef[];
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  // Whether it matters how the owner's part (excess, unassigned rest, fixed
  // share of a shared cost) is split over the units: only when the building
  // has more than one owner (Lewis, 2026-10-17). The key that splits it is
  // then said in a sentence and can be changed.
  ownerPartMatters?: boolean;
  // How many owners the building has, for that sentence.
  ownerCount?: number;
  // Each unit's owner (see the engine's ownerKeyByUnit) and the owners'
  // names: each owner's share of the building by that key under it.
  ownerKeyByUnit?: Record<string, string>;
  ownerNames?: Record<string, string>;
  // A departure settlement: one line, the leaving tenant's own amount from
  // the metering company (see departureMethodsForCategory).
  isDeparture?: boolean;
  // The lines of departed leases whose departure already charged this
  // category: their empty reading is no missing reading (M14).
  excusedKeys?: string[];
  onChange: (patch: Partial<SettlementDraftState>) => void;
  onClose: () => void;
}

// One line per occupant: every tenant of a unit in the period, plus the unit's
// vacant stretches. A reading or a company amount belongs to exactly one
// occupant, so a tenant who moved in mid-year is charged their own figure and
// the empty months go to the owner (see occupantKey in the engine).
interface OccupantLine {
  key: string;
  unitLabel: string;
  // Only on a unit's first line, so the table reads as grouped per unit.
  showUnit: boolean;
  who: string;
  when: string;
  vacant: boolean;
}

const occupantLines = (
  units: UnitRef[],
  contracts: ContractRef[],
  period: { from: string; to: string },
): OccupantLine[] => {
  const periodDays = daysInPeriod(period.from, period.to);
  const vacancyLabel = getLocalizedText("settlement.summary.vacancy");
  const known = new Set(units.map(u => u.id));
  const unitList: UnitRef[] = [
    ...units,
    ...contracts
      .filter(c => !known.has(c.unitId))
      .map(c => ({ id: c.unitId, code: c.unitCode })),
  ].filter((u, i, all) => all.findIndex(x => x.id === u.id) === i);

  const lines: OccupantLine[] = [];
  for (const u of unitList) {
    const unitLabel = u.address ?? u.code;
    const own = contracts
      .filter(
        c =>
          c.unitId === u.id &&
          overlapDays(c.startDate, c.endDate, period.from, period.to) > 0,
      )
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
    const unitLines: OccupantLine[] = own.map(c => ({
      key: occupantKey.contract(c.id),
      unitLabel,
      showUnit: false,
      who: c.tenantName,
      when: rentedPeriodLabel(period, { from: c.startDate, to: c.endDate }),
      vacant: false,
    }));
    const { coveredDays, gaps } = mergedCoverage(own, period);
    if (coveredDays < periodDays) {
      unitLines.push({
        key: occupantKey.vacant(u.id),
        unitLabel,
        showUnit: false,
        who: vacancyLabel,
        when: gaps.map(g => `${fmtDate(g.from)} → ${fmtDate(g.to)}`).join(", "),
        vacant: true,
      });
    }
    if (unitLines.length > 0) unitLines[0].showUnit = true;
    lines.push(...unitLines);
  }
  return lines;
};

// Only per-occupant values survive an edit: a value keyed by a bare unit id
// comes from a draft before this screen worked per occupant and would
// otherwise be counted next to the new lines.
const occupantValues = (
  values: Record<string, number>,
): Record<string, number> =>
  Object.fromEntries(Object.entries(values).filter(([k]) => k.includes(":")));

// Tenants without a meter reading of their own: the engine reads that as 0,
// so they pay nothing and their share ends up in the excess. 0 can be a real
// reading, so this only warns. A line in `excusedKeys` is left out: a
// departed lease whose departure already charged its amount, which its
// empty reading stands for (Y12). By line key, never by name (M14): another
// lease of a tenant with the same name still needs its own reading.
const tenantsWithoutReading = (params: {
  lines: OccupantLine[];
  consumption: Record<string, number>;
  excusedKeys?: string[];
}): string[] =>
  params.lines
    .filter(
      l =>
        !l.vacant &&
        !((params.consumption[l.key] ?? 0) > 0) &&
        !(params.excusedKeys ?? []).includes(l.key),
    )
    .map(l => l.who);

export const tenantsWithoutConsumption = (params: {
  units: UnitRef[];
  contracts: ContractRef[];
  period: { from: string; to: string };
  consumption: Record<string, number>;
  excusedKeys?: string[];
}): string[] =>
  tenantsWithoutReading({
    lines: occupantLines(params.units, params.contracts, params.period),
    consumption: occupantValues(params.consumption),
    excusedKeys: params.excusedKeys,
  });

// The key of a category, as a select: the one that splits the excess over
// the tenants, or the owners' part over the units.
const OwnerPartKeySelect: React.FC<{
  category: Category;
  label: string;
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  onChange: (patch: Partial<SettlementDraftState>) => void;
}> = ({ category, label, keys, draft, onChange }) => (
  <Select
    id={`owner-part-key-${category}`}
    label={label}
    value={draft.selectedKeyId[category] ?? keys[0]?.id}
    options={keys.map(k => ({ value: k.id, label: keyDisplayName(k.name) }))}
    onChange={(opt: any) =>
      onChange({
        selectedKeyId: {
          ...draft.selectedKeyId,
          [category]: opt?.value,
        },
      })
    }
  />
);

// What the owners pay of a category, in plain words, and the key that
// splits it between them (Lewis, 2026-10-17): only in this popup, only with
// more than one owner, never in capitals. Under it, what each owner owns
// of the building by that key (Lewis, 2026-10-19): "Els Eigenaar 55/85 ·
// Mr. Monopoly 30/85".
const OwnerPart: React.FC<{
  category: Category;
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  ownerCount: number;
  ownerSharesOf?: (keyDef: ApportionmentKey) => string | undefined;
  onChange: (patch: Partial<SettlementDraftState>) => void;
}> = ({ category, keys, draft, ownerCount, ownerSharesOf, onChange }) => {
  const keyDef =
    keys.find(k => k.id === draft.selectedKeyId[category]) ?? keys[0];
  const shares = keyDef ? ownerSharesOf?.(keyDef) : undefined;
  return (
    <Stack vertical spacing="tight">
      {keyDef && (
        <div>
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.editor.owner_part_sentence", {
              count: String(ownerCount),
              key: keyDisplayName(keyDef.name),
            })}
          </TextStyle>
          {shares && (
            <div>
              <TextStyle variation="subdued" size="small">
                {shares}
              </TextStyle>
            </div>
          )}
        </div>
      )}
      <OwnerPartKeySelect
        category={category}
        label={getLocalizedText("costs.distribution_method.key")}
        keys={keys}
        draft={draft}
        onChange={onChange}
      />
    </Stack>
  );
};

const WhoCell: React.FC<{ line: OccupantLine }> = ({ line }) => (
  <div>
    {line.vacant ? (
      <TextStyle variation="subdued">{line.who}</TextStyle>
    ) : (
      line.who
    )}
    <div>
      <TextStyle variation="subdued" size="small">
        {line.when}
      </TextStyle>
    </div>
  </div>
);

// One numeric cell, right-aligned to match the amount columns elsewhere. The
// shared Belgian number field reads "1.234,5" as 1234,5 and keeps the typed
// text while the user types; an emptied or unreadable field counts as 0.
const NumField: React.FC<{
  name: string;
  value?: number;
  placeholder: string;
  suffix?: string;
  isMoney?: boolean;
  onChange: (v: number) => void;
}> = ({ name, value, placeholder, suffix, isMoney, onChange }) => (
  <div style={{ maxWidth: 160, marginLeft: "auto" }}>
    <LocaleNumberField
      name={name}
      placeholder={placeholder}
      suffix={suffix}
      isMoney={isMoney}
      maxDecimals={isMoney ? MONEY_DECIMALS : undefined}
      value={value || undefined}
      onChange={next => onChange(next ?? 0)}
    />
  </div>
);

// The same field, full width and with a real label.
const NumTotalField: React.FC<{
  name: string;
  label: string;
  value?: number;
  placeholder: string;
  suffix?: string;
  onChange: (v: number) => void;
}> = ({ name, label, value, placeholder, suffix, onChange }) => (
  <LocaleNumberField
    name={name}
    label={label}
    placeholder={placeholder}
    suffix={suffix}
    value={value || undefined}
    onChange={next => onChange(next ?? 0)}
  />
);

export const DistributionEditorModal: React.FC<
  DistributionEditorModalProps
> = ({
  category,
  method,
  total,
  period,
  contracts,
  units,
  keys,
  draft,
  ownerPartMatters = false,
  ownerCount = 0,
  ownerKeyByUnit,
  ownerNames,
  isDeparture = false,
  excusedKeys,
  onChange,
  onClose,
}) => {
  // Each owner's share of the building by a key, the largest first.
  const ownerSharesOf = (keyDef: ApportionmentKey): string | undefined => {
    if (!ownerKeyByUnit) return undefined;
    const owners = ownersShareByKey({
      keyDef,
      building: { units },
      ownerKeyByUnit,
    });
    if (owners.length < 2) return undefined;
    return owners
      .map(
        owner =>
          `${
            ownerNames?.[owner.ownerKey] ||
            getLocalizedText("costs.split.owner")
          } ${formatShare(owner.weight, owner.total)}`,
      )
      .join(" · ");
  };
  // Only metered categories offer the consumption method; any other one
  // falls back to the word itself, in the user's language.
  const unit =
    CONSUMPTION_UNIT[category] ??
    getLocalizedText("settlement.editor.consumption").toLowerCase();
  // A departure settlement asks only its own tenant's amount: the other
  // units and the vacant days stay for the settlement of the group.
  const lines = React.useMemo(
    () =>
      isDeparture
        ? occupantLines(
            units.filter(u => contracts.some(c => c.unitId === u.id)),
            contracts,
            period,
          ).filter(line => !line.vacant)
        : occupantLines(units, contracts, period),
    [isDeparture, units, contracts, period],
  );

  // Each line's share of the cost as the engine splits it (Y14): its
  // reading, and with the excess on the tenants by the key its part of the
  // excess too, so "Aandeel kost" follows "Overschot ten laste van".
  const consumptionShareOf = (() => {
    if (method !== "consumption" || isDeparture) return undefined;
    const buildingTotal = draft.buildingTotal[category] ?? 0;
    if (!(buildingTotal > 0)) return undefined;
    const keyDef =
      keys.find(k => k.id === draft.selectedKeyId[category]) ??
      keys.find(k => k.isSystem && k.isEqual);
    const res = allocate({
      contracts,
      invoices: [
        {
          id: `editor-${category}`,
          unitId: "",
          scope: "common",
          date: period.to,
          periodFrom: period.from,
          periodTo: period.to,
          category,
          supplier: "",
          amountExclVat: total,
          vatPct: 0,
          target: "settlement",
        },
      ],
      method: "consumption",
      keyWeights: keyDef?.weights ?? {},
      isEqualKey: keyDef ? !!keyDef.isEqual : true,
      consumption: occupantValues(draft.consumption[category] ?? {}),
      buildingTotal,
      excessMode: draft.excessMode[category] ?? "landlord",
      companyAmounts: {},
      period,
      unitIds: units.map(u => u.id),
    });
    return (lineKey: string): number | undefined => {
      if (lineKey.startsWith("contract:")) {
        return res.perContract[lineKey.slice("contract:".length)] ?? 0;
      }
      if (lineKey.startsWith("vacant:")) {
        return res.landlordByUnit[lineKey.slice("vacant:".length)] ?? 0;
      }
      return undefined;
    };
  })();

  const editor = () => {
    if (method === "consumption") {
      return (
        <ConsumptionEditor
          category={category}
          unit={unit}
          total={total}
          shareOf={consumptionShareOf}
          lines={lines}
          keys={keys}
          draft={draft}
          ownerPartMatters={ownerPartMatters}
          ownerCount={ownerCount}
          ownerSharesOf={ownerSharesOf}
          excusedKeys={excusedKeys}
          onChange={onChange}
        />
      );
    }
    if (isDeparture) {
      return (
        <DepartureAmountEditor
          category={category}
          total={total}
          lines={lines}
          tenant={contracts.map(c => c.tenantName).join(", ")}
          draft={draft}
          onChange={onChange}
        />
      );
    }
    return (
      <CompanyEditor
        category={category}
        total={total}
        lines={lines}
        keys={keys}
        draft={draft}
        ownerPartMatters={ownerPartMatters}
        ownerCount={ownerCount}
        ownerSharesOf={ownerSharesOf}
        onChange={onChange}
      />
    );
  };
  const body = editor();

  const methodTitle = () => {
    if (method === "consumption") {
      return getLocalizedText("costs.distribution_method.consumption");
    }
    if (isDeparture) return methodLabel(method, category, { isDeparture });
    return getLocalizedText("settlement.summary.company_amounts");
  };

  return (
    <Modal
      heading={`${CATEGORY_LABELS_NL[category]} — ${methodTitle()}`}
      onClose={onClose}
      width="medium"
      actions={[
        {
          content: getLocalizedText("costs.action.save"),
          appearance: "primary",
          onClick: onClose,
        },
      ]}
    >
      <Stack vertical spacing="loose">
        {body}
      </Stack>
    </Modal>
  );
};

const ConsumptionEditor: React.FC<{
  category: Category;
  unit: string;
  total: number;
  // A line's share as the engine splits it; without it, reading × price.
  shareOf?: (lineKey: string) => number | undefined;
  lines: OccupantLine[];
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  ownerPartMatters: boolean;
  ownerCount: number;
  ownerSharesOf: (keyDef: ApportionmentKey) => string | undefined;
  excusedKeys?: string[];
  onChange: (patch: Partial<SettlementDraftState>) => void;
}> = ({
  category,
  unit,
  total,
  shareOf,
  lines,
  keys,
  draft,
  ownerPartMatters,
  ownerCount,
  ownerSharesOf,
  excusedKeys,
  onChange,
}) => {
  const cons = occupantValues(draft.consumption[category] ?? {});
  // Only once readings are being entered: an empty editor is not a warning.
  const missingTenants = Object.values(cons).some(v => v > 0)
    ? tenantsWithoutReading({ lines, consumption: cons, excusedKeys })
    : [];
  const buildingTotal = draft.buildingTotal[category] ?? 0;
  const sumInd = lines.reduce((s, l) => s + (cons[l.key] ?? 0), 0);
  const excess = Math.max(0, buildingTotal - sumInd);
  const ratio = buildingTotal > 0 ? total / buildingTotal : 0;
  const excessMode = draft.excessMode[category] ?? "landlord";

  return (
    <Stack vertical spacing="loose">
      <TextStyle variation="subdued">
        {getLocalizedText("settlement.editor.consumption_intro")}
      </TextStyle>

      <NumTotalField
        name={`cons-total-${category}`}
        label={getLocalizedText("settlement.editor.total_meter_consumption", {
          unit,
        })}
        placeholder={getLocalizedText(
          "settlement.editor.total_meter_placeholder",
        )}
        suffix={unit}
        value={buildingTotal}
        onChange={(val: number) =>
          onChange({
            buildingTotal: { ...draft.buildingTotal, [category]: val },
          })
        }
      />

      <DataTable
        columnContentTypes={["text", "text", "numeric", "numeric"]}
        headings={[
          getLocalizedText("costs.field.property"),
          getLocalizedText("costs.field.tenant"),
          getLocalizedText("settlement.editor.consumption_with_unit", { unit }),
          getLocalizedText("settlement.editor.cost_share"),
        ]}
        emptyState={
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.editor.no_tenants_in_period")}
          </TextStyle>
        }
        rows={lines.map(l => ({
          id: l.key,
          content: [
            l.showUnit ? l.unitLabel : "",
            <WhoCell line={l} />,
            <NumField
              name={`cons-${category}-${l.key}`}
              value={cons[l.key]}
              placeholder={getLocalizedText("settlement.editor.consumption")}
              suffix={unit}
              onChange={(val: number) =>
                onChange({
                  consumption: {
                    ...draft.consumption,
                    [category]: { ...cons, [l.key]: val },
                  },
                })
              }
            />,
            buildingTotal > 0 ? (
              moneyCell(shareOf?.(l.key) ?? (cons[l.key] ?? 0) * ratio)
            ) : (
              <TextStyle variation="subdued">—</TextStyle>
            ),
          ],
        }))}
      />

      {missingTenants.length > 0 && (
        <Banner variation="warning" hasDismiss={false}>
          {getLocalizedText(
            missingTenants.length === 1
              ? "settlement.editor.consumption_missing_one"
              : "settlement.editor.consumption_missing_many",
            {
              names: missingTenants.join(", "),
              amount: fmt(0),
              category: CATEGORY_LABELS_NL[category],
            },
          )}
        </Banner>
      )}

      {excess > 0 && (
        <Stack vertical spacing="tight">
          <Banner variation="warning" hasDismiss={false}>
            {getLocalizedText("settlement.editor.excess_warning", {
              amount: formatNumber(+excess.toFixed(2), false),
              unit,
            })}
          </Banner>
          <Select
            id={`excess-${category}`}
            label={getLocalizedText("settlement.editor.excess_charged_to")}
            value={excessMode}
            options={[
              {
                value: "landlord",
                label: getLocalizedText("costs.field.owner"),
              },
              {
                value: "key",
                label: getLocalizedText("settlement.editor.tenants_by_key"),
              },
            ]}
            onChange={(opt: any) =>
              onChange({
                excessMode: {
                  ...draft.excessMode,
                  [category]: (opt?.value ?? "landlord") as "key" | "landlord",
                },
              })
            }
          />
          {/* "By key" needs a key: without this choice the excess fell back
              to an equal split per unit, whatever the group's own key said. */}
          {excessMode === "key" && keys.length > 0 && (
            <OwnerPartKeySelect
              category={category}
              label={getLocalizedText("costs.distribution_method.key")}
              keys={keys}
              draft={draft}
              onChange={onChange}
            />
          )}
        </Stack>
      )}
      {/* An excess for the owner (or a fixed owner share) is split over the
          units by this key too; with several owners that decides who pays
          what, so it is no longer a hidden choice. */}
      {(excess <= 0 || excessMode === "landlord") &&
        ownerPartMatters &&
        keys.length > 0 && (
          <OwnerPart
            category={category}
            keys={keys}
            draft={draft}
            ownerCount={ownerCount}
            ownerSharesOf={ownerSharesOf}
            onChange={onChange}
          />
        )}
    </Stack>
  );
};

const CompanyEditor: React.FC<{
  category: Category;
  total: number;
  lines: OccupantLine[];
  keys: ApportionmentKey[];
  draft: SettlementDraftState;
  ownerPartMatters: boolean;
  ownerCount: number;
  ownerSharesOf: (keyDef: ApportionmentKey) => string | undefined;
  onChange: (patch: Partial<SettlementDraftState>) => void;
}> = ({
  category,
  total,
  lines,
  keys,
  draft,
  ownerPartMatters,
  ownerCount,
  ownerSharesOf,
  onChange,
}) => {
  const amts = occupantValues(draft.companyAmounts[category] ?? {});
  const sum = lines.reduce((s, l) => s + (amts[l.key] ?? 0), 0);
  const delta = +(sum - total).toFixed(2);
  const ok = Math.abs(delta) < 0.01;

  return (
    <Stack vertical spacing="loose">
      <TextStyle variation="subdued">
        {getLocalizedText("settlement.editor.company_intro")}
      </TextStyle>

      <DataTable
        columnContentTypes={["text", "text", "numeric"]}
        headings={[
          getLocalizedText("costs.field.property"),
          getLocalizedText("costs.field.tenant"),
          getLocalizedText("settlement.editor.charged_amount"),
        ]}
        rows={lines.map(l => ({
          id: l.key,
          content: [
            l.showUnit ? l.unitLabel : "",
            <WhoCell line={l} />,
            <NumField
              name={`company-${category}-${l.key}`}
              value={amts[l.key]}
              placeholder={getLocalizedText("costs.field.amount")}
              suffix="€"
              isMoney
              onChange={(val: number) =>
                onChange({
                  companyAmounts: {
                    ...draft.companyAmounts,
                    [category]: { ...amts, [l.key]: val },
                  },
                })
              }
            />,
          ],
        }))}
      />

      <Banner variation={ok ? "success" : "warning"} hasDismiss={false}>
        {ok
          ? getLocalizedText("settlement.editor.sum_ok", {
              sum: fmt(sum),
              total: fmt(total),
            })
          : getLocalizedText(
              delta > 0
                ? "settlement.editor.sum_too_much"
                : "settlement.editor.sum_too_little",
              {
                sum: fmt(sum),
                total: fmt(total),
                delta: fmt(Math.abs(delta)),
              },
            )}
      </Banner>
      {ownerPartMatters && keys.length > 0 && (
        <OwnerPart
          category={category}
          keys={keys}
          draft={draft}
          ownerCount={ownerCount}
          ownerSharesOf={ownerSharesOf}
          onChange={onChange}
        />
      )}
    </Stack>
  );
};

// A departure settlement's own line: the amount the metering company
// calculated for the leaving tenant, e.g. in an interim statement. It cannot
// be more than the category's costs; the rest stays open for the settlement
// of the group, which gives the owner any difference with the yearly figures.
const DepartureAmountEditor: React.FC<{
  category: Category;
  total: number;
  lines: OccupantLine[];
  tenant: string;
  draft: SettlementDraftState;
  onChange: (patch: Partial<SettlementDraftState>) => void;
}> = ({ category, total, lines, tenant, draft, onChange }) => {
  const amts = occupantValues(draft.companyAmounts[category] ?? {});
  const amount = sumInCents(lines.map(l => amts[l.key] ?? 0));
  const totalCents = toCents(total);

  return (
    <Stack vertical spacing="loose">
      <TextStyle variation="subdued">
        {getLocalizedText("settlement.departure.tenant_amount_intro", {
          tenant,
        })}
      </TextStyle>

      <DataTable
        columnContentTypes={["text", "text", "numeric"]}
        headings={[
          getLocalizedText("costs.field.property"),
          getLocalizedText("costs.field.tenant"),
          getLocalizedText("settlement.editor.charged_amount"),
        ]}
        emptyState={
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.editor.no_tenants_in_period")}
          </TextStyle>
        }
        rows={lines.map(l => ({
          id: l.key,
          content: [
            l.unitLabel,
            <WhoCell line={l} />,
            <NumField
              name={`company-${category}-${l.key}`}
              value={amts[l.key]}
              placeholder={getLocalizedText("costs.field.amount")}
              suffix="€"
              isMoney
              onChange={(val: number) =>
                onChange({
                  companyAmounts: {
                    ...draft.companyAmounts,
                    [category]: { ...amts, [l.key]: val },
                  },
                })
              }
            />,
          ],
        }))}
      />

      {amount > totalCents && (
        <Banner variation="warning" hasDismiss={false}>
          {getLocalizedText("settlement.editor.sum_too_much", {
            sum: fmt(fromCents(amount)),
            total: fmt(total),
            delta: fmt(fromCents(amount - totalCents)),
          })}
        </Banner>
      )}
      {amount > 0 && amount <= totalCents && (
        <Banner variation="info" hasDismiss={false}>
          {getLocalizedText("settlement.departure.tenant_amount_rest", {
            amount: fmt(fromCents(amount)),
            tenant,
            rest: fmt(fromCents(totalCents - amount)),
          })}
        </Banner>
      )}
    </Stack>
  );
};
